import * as React from "react";

import { Point } from "@1js/window-util";

import { AppShyComponentProps, PopOverState } from "./AppShyComponent.props";
import {
  AppShyComponentClassNames,
  getShyComponentClassNames,
} from "./AppShyComponent.styles";
// eslint-disable-next-line no-restricted-imports
import { arrayify } from "@1js/acui-common/lib/utilities/arrayify";

export class AppShyComponent extends React.PureComponent<
  AppShyComponentProps,
  {}
> {
  private triggerEventsRegistered: boolean = false;
  private shyDismissalRegionEventsRegistered: boolean = false;
  private shyComponent = React.createRef<HTMLDivElement>();

  public render() {
    const classNames = getShyComponentClassNames(
      this.isVisible(),
      this.props.styles
    );
    return (
      <div
        id="appshycomponent"
        {...this.getDivContainerProps(classNames)}
        ref={this.shyComponent}
      >
        {this.props.children}
      </div>
    );
  }

  public componentDidMount() {
    if (this.props.isActive) {
      this.registerEventsOnTriggerElements();
      this.registerForTransitionEvents();
    }
  }

  public componentDidUpdate(prevProps: AppShyComponentProps) {
    // Register events if we're newly active
    if (!prevProps?.isActive && this.props.isActive) {
      this.registerEventsOnTriggerElements();
      this.registerForTransitionEvents();
    }

    // Unregister actions if we're newly inactive
    if (prevProps?.isActive && !this.props.isActive) {
      this.unregisterEventsOnTriggerElements();
      this.unregisterForTransitionEvents();
    }
  }

  public componentWillUnmount() {
    this.unregisterEventsOnTriggerElements();
    this.unregisterForTransitionEvents();
  }

  /**
   * If we render the AppShyComponent's containing div conditionally (add/remove it when shy state is toggled), the refs used by getShyComponentDiv
   * get overwritten. Instead, we're going to keep the containing div, and populate it only if shy state is active
   */
  private getDivContainerProps = (
    classNames: AppShyComponentClassNames
  ): any => {
    if (this.props.isActive) {
      return {
        className: classNames.container,
        onMouseLeave: this.onMouseLeave,
        onMouseDown: this.onMouseDown,
        onMouseEnter: this.onMouseEnter,
        onTouchStart: this.onTouchStart,
      };
    }
    return undefined;
  };

  private registerForTransitionEvents() {
    if (this.props.onTransitionStart) {
      this.shyComponent.current?.addEventListener(
        "transitionstart",
        this.props.onTransitionStart
      );
    }
    if (this.props.onTransitionEnd) {
      this.shyComponent.current?.addEventListener(
        "transitionend",
        this.props.onTransitionEnd
      );
    }
  }

  private unregisterForTransitionEvents() {
    if (this.props.onTransitionStart) {
      this.shyComponent.current?.removeEventListener(
        "transitionstart",
        this.props.onTransitionStart
      );
    }
    if (this.props.onTransitionEnd) {
      this.shyComponent.current?.removeEventListener(
        "transitionend",
        this.props.onTransitionEnd
      );
    }
  }

  /**
   * Pointer down on regions that should dismiss the shy component
   */
  private onPointerDown = (event: PointerEvent) => {
    if (
      this.props.popOverState !== PopOverState.Collapsed &&
      !this.coordinateIsWithinShyComponentOrTriggerElement({
        x: event.clientX,
        y: event.clientY,
      })
    ) {
      this.props.onPopOverStateChange(
        PopOverState.Collapsed,
        this.props.popOverState
      );
    }
  };

  /**
   * Touching/Mousing over the div will invoke the shy component when hidden
   */
  private onTouchStart = (ev: React.TouchEvent<HTMLElement> | TouchEvent) => {
    if (
      this.props.isActive &&
      !this.isVisible() &&
      this.coordinateIsWithinShyComponentOrTriggerElement({
        x: ev.touches[0].clientX,
        y: ev.touches[0].clientY,
      })
    ) {
      this.props.onPopOverStateChange(
        PopOverState.Expanded,
        this.props.popOverState
      );
    }
  };

  private onMouseEnter = (
    ev: React.MouseEvent<HTMLDivElement> | MouseEvent
  ) => {
    if (
      this.props.isActive &&
      !this.isVisible() &&
      this.coordinateIsWithinShyComponentOrTriggerElement({
        x: ev.clientX,
        y: ev.clientY,
      })
    ) {
      this.props.onPopOverStateChange(
        PopOverState.ExpandedSoftDismiss,
        this.props.popOverState
      );
    }
  };

  /**
   * If we are in the ExpandedSoftDismiss state, mouseleave should dismiss the component if pointer is now outside
   * both the shy component bounds AND the triggerElement (if props.triggerElement is defined)
   */
  private onMouseLeave = (
    ev: React.MouseEvent<HTMLDivElement> | React.MouseEvent | MouseEvent
  ) => {
    if (
      this.props.popOverState === PopOverState.ExpandedSoftDismiss &&
      !this.coordinateIsWithinShyComponentOrTriggerElement({
        x: ev.clientX,
        y: ev.clientY,
      })
    ) {
      this.props.onPopOverStateChange(
        PopOverState.Collapsed,
        this.props.popOverState
      );
    }
  };

  /**
   * If we are in the ExpandedSoftDismiss state, interacting with the component should disable soft dismiss
   */
  private onMouseDown = () => {
    if (this.props.popOverState === PopOverState.ExpandedSoftDismiss) {
      this.props.onPopOverStateChange(
        PopOverState.Expanded,
        this.props.popOverState
      );
    }
  };

  /**
   * Returns true if a given coordinate is within the shy component element or the trigger element.  We want to scope
   * this to leaving at the bottom to not effect elements above the shy component.
   */
  private coordinateIsWithinShyComponentOrTriggerElement = (point: Point) => {
    let withinTriggerElem = arrayify(this.props.triggerElement).some(
      (triggerElementId: string) => {
        const triggerElement = document.getElementById(triggerElementId);
        let triggerElementRect;
        if (triggerElement) {
          triggerElementRect =
            triggerElement.getBoundingClientRect() as DOMRect;
        }
        return (
          triggerElementRect && this.isPointInRect(point, triggerElementRect)
        );
      }
    );

    const shyComponentRect =
      this.shyComponent.current?.getBoundingClientRect() as DOMRect;
    const withinShyComponentElem =
      shyComponentRect && this.isPointInRect(point, shyComponentRect);

    return withinShyComponentElem || withinTriggerElem;
  };

  private isPointInRect = (point: Point, rect: DOMRect) => {
    return !(
      point.y < rect.top ||
      point.y > rect.bottom ||
      point.x < rect.left ||
      point.x > rect.right
    );
  };

  private registerEventsOnTriggerElements = () => {
    if (this.props.triggerElement && !this.triggerEventsRegistered) {
      arrayify(this.props.triggerElement).forEach((triggerElement: string) => {
        const htmlElement = document.getElementById(triggerElement);
        htmlElement && this.registerEventsOnTriggerElement(htmlElement);
      });
    }

    if (
      this.props.shyDismissalRegions &&
      !this.shyDismissalRegionEventsRegistered
    ) {
      this.props.shyDismissalRegions.forEach((region) =>
        region.addEventListener("pointerdown", this.onPointerDown)
      );
      this.shyDismissalRegionEventsRegistered = true;
    }
  };

  private registerEventsOnTriggerElement = (element: HTMLElement) => {
    element.addEventListener("mouseenter", this.onMouseEnter);
    element.addEventListener("touchstart", this.onTouchStart);
    element.addEventListener("mouseleave", this.onMouseLeave);
  };

  private unregisterEventsOnTriggerElements = () => {
    if (this.props.triggerElement && !this.triggerEventsRegistered) {
      arrayify(this.props.triggerElement).forEach((triggerElement: string) => {
        const htmlElement = document.getElementById(triggerElement);
        htmlElement && this.unregisterEventsOnTriggerElement(htmlElement);
      });
    }

    if (
      this.props.shyDismissalRegions &&
      this.shyDismissalRegionEventsRegistered
    ) {
      this.props.shyDismissalRegions.forEach((region) =>
        region.removeEventListener("pointerdown", this.onPointerDown)
      );
      this.shyDismissalRegionEventsRegistered = false;
    }
  };

  private unregisterEventsOnTriggerElement = (element: HTMLElement) => {
    element.removeEventListener("mouseenter", this.onMouseEnter);
    element.removeEventListener("touchstart", this.onTouchStart);
    element.removeEventListener("mouseleave", this.onMouseLeave);
  };

  /**
   * Returns true if the component is currently visible
   */
  public isVisible = () => {
    return (
      this.props.popOverState === PopOverState.Expanded ||
      this.props.popOverState === PopOverState.ExpandedSoftDismiss ||
      this.props.popOverState === PopOverState.Pinned
    );
  };
}
